import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import type { Category } from "../api/types";
import { vibrate } from "../components/Bits";
import { Steps } from "../components/Steps";
import { emptyPiece, rowDone, rowsFor } from "../lib/piece";
import { useApp } from "../state";
import { DescribePiece, describePiece } from "./DescribePiece";

/** What the favorite is, in one row of chips: the kind picks the category too. */
const FAV_KINDS: [string, string, Category][] = [
  ["jeans", "Jeans", "pants"],
  ["pants", "Pants", "pants"],
  ["shorts", "Shorts", "pants"],
  ["top", "Top", "top"],
  ["dress", "Dress", "dress"],
  ["skirt", "Skirt", "skirt"],
];

/** Step 1: the piece you love, saved to your profile before you shop. Brand and size are all it needs. */
export function FavoriteStep() {
  const { meta, fav, setFav, draft } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { cat, fields } = fav;
  const rows = rowsFor(cat);
  const answered = rows.filter((r) => rowDone(fields, r)).length;
  const ready = rowDone(fields, "brand");

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { product, size, fit, wouldChange, fitWish } = await describePiece(cat, fields);
      const { favorite } = await api.addFavorite({
        product_id: product.id,
        size_label: size,
        ...(fit ? { variant: fit } : {}),
        ...(wouldChange.length ? { would_change: wouldChange } : {}),
        ...(fitWish ? { fit_wish: fitWish } : {}),
      });
      if (fav.savedId) await api.deleteFavorite(fav.savedId).catch(() => {}); // an edit replaces the one saved before
      setFav((d) => ({ ...d, savedId: favorite.id }));
      vibrate();
      nav("/", { state: { savedFavorite: favorite.id } });
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="view enter fav-step">
      <Steps at={1} favDone={!!fav.savedId} eyeingDone={!!draft.cand} answered={answered + (draft.cand ? 1 : 0)} questions={rows.length} />
      <h1>
        <span className="ph">Start with a piece</span> <span className="ph">you <em>love</em> wearing.</span>
      </h1>
      <div className="row" role="group" aria-label="What is it?">
        <span className="eyebrow" style={{ marginRight: 4 }}>
          My favorite
        </span>
        {FAV_KINDS.map(([k, name, c]) => (
          <button
            key={k}
            type="button"
            className="chip sm"
            aria-pressed={fav.kind === k}
            onClick={() =>
              setFav((d) => {
                if (d.kind === k) return d;
                // Same category: keep the answers. A new one: brand carries over, the rest starts fresh.
                const kind = c === "pants" ? k : null;
                const fields = c === d.cat ? { ...d.fields, kind } : { ...emptyPiece(kind), brand: d.fields.brand };
                return { ...d, cat: c, kind: k, fields };
              })
            }
          >
            {name}
          </button>
        ))}
      </div>
      <div className="stack tight">
        <p className="eyebrow">{rows.length} questions · about 20 seconds</p>
        <DescribePiece
          key={cat}
          cat={cat}
          value={fields}
          onChange={(f) => setFav((d) => ({ ...d, fields: f(d.fields) }))}
          brands={meta.brands ?? []}
          tailoring={meta.categories[cat].tailoring_options}
          wouldChange={meta.categories[cat].would_change}
        />
      </div>
      <div className="stack">
        <button className="btn primary" type="button" disabled={!ready || busy} onClick={() => void save()}>
          {fav.savedId ? "Save changes" : "Save to my profile"}
        </button>
        {err && (
          <p className="hint warn center" role="alert">
            {err}
          </p>
        )}
        <p className="aside center">{ready ? (answered < rows.length ? "Ready to save. The rest is optional." : "All set.") : "Brand and size are all we need."}</p>
      </div>
    </section>
  );
}
